'use client'

import * as React from 'react'
import { OTPInput as OTPInputPrimitive, OTPInputContext } from 'input-otp'
import { Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

// ==================== Base Components (from shadcn) ====================

const InputOTPBase = React.forwardRef<
  React.ElementRef<typeof OTPInputPrimitive>,
  React.ComponentPropsWithoutRef<typeof OTPInputPrimitive>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInputPrimitive
    ref={ref}
    containerClassName={cn(
      'flex items-center gap-2 has-[:disabled]:opacity-50',
      containerClassName
    )}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
))
InputOTPBase.displayName = 'InputOTPBase'

const InputOTPGroup = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center', className)} {...props} />
))
InputOTPGroup.displayName = 'InputOTPGroup'

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index]

  return (
    <div
      ref={ref}
      data-active={isActive}
      data-filled={!!char}
      className={cn(
        'relative flex h-9 w-9 items-center justify-center border-y border-r border-input text-sm shadow-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md',
        isActive && 'z-10 ring-1 ring-ring',
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  )
})
InputOTPSlot.displayName = 'InputOTPSlot'

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <Minus />
  </div>
))
InputOTPSeparator.displayName = 'InputOTPSeparator'

// ==================== Styled OTPInput Component ====================

interface OTPInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  error?: boolean
  autoFocus?: boolean
}

function InputOtp({
  value = '',
  onChange,
  length = 6,
  disabled = false,
  error = false,
  autoFocus = false,
}: OTPInputProps) {
  return (
    <InputOTPBase
      maxLength={length}
      value={value}
      onChange={onChange}
      disabled={disabled}
      autoFocus={autoFocus}
      containerClassName="justify-center"
    >
      <InputOTPGroup className="gap-2 sm:gap-2">
        {Array.from({ length }).map((_, index) => (
          <InputOTPSlot
            key={index}
            index={index}
            className={cn(
              // Base styles - individual rounded boxes
              'w-11 h-14 sm:w-10 sm:h-12 text-xl sm:text-lg font-semibold rounded-lg border-2 bg-background',
              // Override default shadcn connected border style
              'first:rounded-lg last:rounded-lg first:border-l-2 border-l-2',
              // Active/focus styles (using data attribute)
              'data-[active=true]:z-10 data-[active=true]:ring-2 data-[active=true]:ring-primary data-[active=true]:border-primary',
              // Error styles
              error
                ? 'border-destructive data-[active=true]:ring-destructive data-[active=true]:border-destructive'
                : 'data-[filled=true]:border-primary border-border'
            )}
          />
        ))}
      </InputOTPGroup>
    </InputOTPBase>
  )
}

export {
  InputOtp,
  // Base components for custom implementations
  InputOTPBase,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
}
